import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDebounce } from "use-debounce";
import { infiniteScrollHook } from "../../hooks/infiniteScrollHook";
import TenantListingTopHeader from "../../newComponents/listings/top-listing-headers/tenant-listing-top-header";
import CurrentTenantCard from "../../newComponents/listings/listing-cards/current-tenant";
import ProspectTenantCard from "../../newComponents/listings/listing-cards/prospect-tenant";
import { Route, Routes, useLocation, useNavigate } from "react-router";
import AddTenant from "../../newComponents/tenant/AddTenant";
import EditTenant from "../../newComponents/tenant/EditTenant";
import { TenantStatusesEnum } from "../../components/common/Enum";
import { LeaseApplicationService, TenantService } from "@propertelligent/client-api";
import CurrentTenantDetails2 from "../../newComponents/tenant/CurrentTenantDetails";
import LandlordTenantOnboard from "../../components/tenants/landlordTenantOnboard";
import RenewalLease from "../../components/tenants/RenewalLease";
import MoveInAndOutContentBlocks from "../../components/tenants/common/MoveInAndOutContentBlocks";
import LeaseApplicationReviewView from "../../newComponents/tenant/ReviewApplication";
import LeaseAgreementReviewView from "../../newComponents/tenant/ReviewAgreement";
import { usePermissions } from "../../hooks/usePermissions";
import NoAccess from "../NoAccess";
import { PermissionGuard } from "../../newComponents/PermissionGuard";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { PubSub, TopicsEnum } from "../../utils/pubsub";
import apiHelper from "../../utils/apiHelper";
import { UserLoginData } from "../../components/common/charts/Enums";
import { ALL_FILTER_KEY, getVisibleAggregateOptions, resolveAllFilterCount, useAggregateFilterCounts } from "../../hooks/useAggregateFilterCounts";
import {
  getApiTenantOrganizationlistCounts,
  getApiTenantPropertylistCounts,
} from "../../services/listFilterCounts.service";
import { escapeDynamicLinqStringLiteral } from "../../utils/dynamicLinq";

// Define the props interface for the MainContent component
interface MainContentProps {
  className?: string;
  handleSort: () => void;
  currentSortDirection: string;
  handleSelectChange: (value: string, label: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filterOptions: { title: string; count: number }[];
  secondaryFilterOptions: { title: string; count: number }[];
  setActiveTab: (tab: string) => void;
  activeTab: string;
  selectedFilterLabel: string;
  commonProps: any;
  detailsActiveTab?: string;
  hasAnyTenants: boolean;
  totalItemCount: number;
  showAddButton?: boolean;
}

const MainContent = ({
  className,
  handleSort,
  currentSortDirection,
  handleSelectChange,
  searchTerm,
  setSearchTerm,
  filterOptions,
  secondaryFilterOptions,
  setActiveTab,
  activeTab,
  selectedFilterLabel,
  commonProps,
  detailsActiveTab,
  hasAnyTenants,
  totalItemCount,
  showAddButton = true,
}: MainContentProps) => {
  const navigate = useNavigate();

  return (
    <div className="content-wrapper container-fluid">
      {hasAnyTenants && (
        <TenantListingTopHeader
          className={className}
          PageHeading="Tenants"
          ButtonName="Add Tenant"
          handleSort={handleSort}
          currentSortDirection={currentSortDirection}
          handleSelectChange={handleSelectChange}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterOptions={filterOptions}
          secondaryFilterOptions={secondaryFilterOptions}
          setActiveTab={setActiveTab}
          activeTab={activeTab}
          selectedFilterLabel={selectedFilterLabel}
          totalItemCount={totalItemCount}
          showAddButton={showAddButton}
          onClick={() =>
            navigate("/tenants/create", {
              state: { from: window.location.pathname, activeTab: detailsActiveTab },
            })
          }
        />
      )}
      {activeTab === "Current" && <CurrentTenantCard Id="CurrentTenant" {...commonProps} hasAnyTenants={hasAnyTenants} className={className} />}
      {activeTab === "Prospect" && <ProspectTenantCard Id="ProspectTenant" {...commonProps} hasAnyTenants={hasAnyTenants} className={className} />}
    </div>
  );
};

interface TenantsProps {
  isEntity?: boolean;
  entityId?: number;
  className?: string;
  activeTab?: string;
  detailsActiveTab?: string;
}

const Tenants = ({ isEntity, entityId, className, detailsActiveTab }: TenantsProps) => {






  const { hasAccess, canWrite } = usePermissions();

  if (!hasAccess("Tenant", 1)) {
    return <NoAccess />;
  }

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const initialSelectedOption = queryParams.get("selectedOption")
    ? Number(queryParams.get("selectedOption"))
    : TenantStatusesEnum.Current;

  const [activeTab, setActiveTab] = useState(
    initialSelectedOption === TenantStatusesEnum.Current ? "Current" :
      initialSelectedOption === TenantStatusesEnum.Prospect ? "Prospect" :
        "Past"
  );

  const [filterKey, setFilterKey] = useState(queryParams.get("filterKey") || null);
  const [selectedFilterLabel, setSelectedFilterLabel] = useState(queryParams.get("filterLabel") || "All");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const emptyListResponse = { success: [], recordCount: 0, aggregateValues: {} };
  const currentSortRef = useRef("asc");
  const prospectSortRef = useRef("asc");
  const skipActiveTabRefetch = useRef(true);

  const currentSearchOnlyFilter = useMemo(
    () =>
      debouncedSearchTerm
        ? `(User.FirstName + " " + User.LastName).ToLower().Contains("${escapeDynamicLinqStringLiteral(debouncedSearchTerm.toLowerCase())}")`
        : null,
    [debouncedSearchTerm],
  );

  const prospectSearchOnlyFilter = useMemo(
    () =>
      debouncedSearchTerm
        ? `(Tenant.User.FirstName + " " + Tenant.User.LastName).ToLower().Contains("${escapeDynamicLinqStringLiteral(debouncedSearchTerm.toLowerCase())}")`
        : null,
    [debouncedSearchTerm],
  );

  const getCurrentApiFunction = useCallback(
    () => async (
      organizationId: number,
      pageSize?: number,
      pageNumber?: number,
      skipValue?: number,
      filterEntityTypeId?: any,
      filterEntityIds?: Array<number>,
      fullyQualifiedFilters?: string,
    ) => {
      if (isEntity && !entityId) {
        console.error("Error: entityId is required when isEntity is true");
        return emptyListResponse;
      }

      const effectiveFilterKey = activeTab === "Current" ? filterKey : null;
      const sort = currentSortRef.current !== "none"
        ? `User.FirstName ${currentSortRef.current}`
        : undefined;

      try {
        if (isEntity) {
          return effectiveFilterKey
            ? await TenantService.getApiTenantPropertylist1(entityId!, effectiveFilterKey, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, sort)
            : await TenantService.getApiTenantPropertylist(entityId!, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, sort);
        }
        return effectiveFilterKey
          ? await TenantService.getApiTenantOrganizationlist1(organizationId, effectiveFilterKey, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, sort)
          : await TenantService.getApiTenantOrganizationlist(organizationId, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, sort);
      } catch (error) {
        console.error("Error fetching current tenant data:", error);
        return emptyListResponse;
      }
    },
    [isEntity, entityId, filterKey, activeTab]
  );

  const getProspectApiFunction = useCallback(
    () => async (
      organizationId: number,
      pageSize?: number,
      pageNumber?: number,
      skipValue?: number,
      filterEntityTypeId?: any,
      filterEntityIds?: Array<number>,
      fullyQualifiedFilters?: string,
    ) => {
      if (isEntity && !entityId) {
        console.error("Error: entityId is required when isEntity is true");
        return emptyListResponse;
      }

      const effectiveFilterKey = activeTab === "Prospect" ? filterKey : null;
      const sort = prospectSortRef.current !== "none"
        ? `Tenant.User.FirstName ${prospectSortRef.current}`
        : undefined;

      try {
        if (isEntity) {
          return effectiveFilterKey
            ? await LeaseApplicationService.getApiLeaseApplicationPropertylist1(entityId!, effectiveFilterKey, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, sort)
            : await LeaseApplicationService.getApiLeaseApplicationPropertylist(entityId!, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, sort);
        }
        return effectiveFilterKey
          ? await LeaseApplicationService.getApiLeaseApplicationOrganizationlist1(organizationId, effectiveFilterKey, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, sort)
          : await LeaseApplicationService.getApiLeaseApplicationOrganizationlist(organizationId, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, sort);
      } catch (error) {
        console.error("Error fetching prospect tenant data:", error);
        return emptyListResponse;
      }
    },
    [isEntity, entityId, filterKey, activeTab]
  );

  const currentTenantData = infiniteScrollHook({
    httpFetchDataFn: getCurrentApiFunction(),
    FullyQualifiedFilters: activeTab === "Current" ? currentSearchOnlyFilter : null,
    pageSize: 10,
    skipAutoFetch: true,
  });

  const prospectTenantData = infiniteScrollHook({
    httpFetchDataFn: getProspectApiFunction(),
    FullyQualifiedFilters: activeTab === "Prospect" ? prospectSearchOnlyFilter : null,
    pageSize: 10,
    skipAutoFetch: true,
  });

  currentSortRef.current = currentTenantData.currentSortDirection;
  prospectSortRef.current = prospectTenantData.currentSortDirection;

  const activeTenantData = activeTab === "Current" ? currentTenantData : prospectTenantData;
  const {
    rows,
    loading,
    hasMore,
    rowCount,
    fetchData,
    currentSortDirection,
    handleSort,
    currentPage,
    aggregateValues,
  } = activeTenantData;

  // Stable Current/Prospect totals + sub-filter breakdown, fetched unfiltered (pageSize 1)
  // so they don't get overwritten when a sub-filter (Lease Expiring Soon, Past, etc.) is
  // applied to the row list — see fetchTabCounts below.
  const [tabTotalCounts, setTabTotalCounts] = useState<{ Current: number; Prospect: number }>({ Current: 0, Prospect: 0 });
  const [baselineTabTotalCounts, setBaselineTabTotalCounts] = useState<{ Current: number; Prospect: number }>({ Current: 0, Prospect: 0 });
  // Prospect-tab status buckets ("In Progress" / "Submitted" / "Approved" / "Past" -- see
  // LeaseApplicationController.GetFilterKeyValuePairs on the backend). The Prospect row-fetch
  // (getApiLeaseApplication{Organization,Property}list) already returns these in its
  // aggregateValues, search-scoped but never pill-scoped (fetchTabCounts never passes
  // filterKey) -- exactly the "search-only refetch" shape the secondary-filter dropdown needs.
  const [prospectAggregateValues, setProspectAggregateValues] = useState<Record<string, number> | undefined>(undefined);

  // Known drift from the plan's "single counts map" design: Current/Prospect *tab* totals and
  // the Prospect secondary dropdown come from these row-list requests (fetchTabCounts) rather
  // than a `.../counts` endpoint, because LeaseApplication has no counts sibling yet and the
  // Tenant Current/Prospect split isn't itself an aggregate bucket. Only the Current tab's
  // *secondary* status dropdown uses the dedicated `useAggregateFilterCounts` hook below
  // (`fetchTenantCounts`), which keeps fetching even while the Prospect tab is active since
  // `scopeKey` doesn't vary by tab -- a `LeaseApplication .../counts` sibling would let this
  // collapse onto one map for both tabs. Follow-up, not required for this PR.
  const fetchTabCounts = useCallback(async () => {
    if (isEntity && !entityId) return;

    try {
      const orgIdStr = sessionStorage.getItem(UserLoginData.organization_id);
      const tenantIdStr = sessionStorage.getItem(UserLoginData.tenant_id);
      const orgId = orgIdStr ? parseInt(orgIdStr) : 0;
      const tenantId = tenantIdStr ? parseInt(tenantIdStr) : 0;
      const reqID = orgId ? orgId : tenantId;
      const scopeId = isEntity ? entityId! : reqID;
      const currentApi = isEntity ? TenantService.getApiTenantPropertylist : TenantService.getApiTenantOrganizationlist;
      const prospectApi = isEntity
        ? LeaseApplicationService.getApiLeaseApplicationPropertylist
        : LeaseApplicationService.getApiLeaseApplicationOrganizationlist;
      const loadCurrent = (filter: string | null) =>
        apiHelper(
          currentApi,
          { showNotification: false },
          scopeId,
          1,
          1,
          undefined,
          undefined,
          undefined,
          filter,
        );
      const loadProspect = (filter: string | null) =>
        apiHelper(
          prospectApi,
          { showNotification: false },
          scopeId,
          1,
          1,
          undefined,
          undefined,
          undefined,
          filter,
        );
      const hasSearchFilter = Boolean(currentSearchOnlyFilter || prospectSearchOnlyFilter);

      // With no active search, the live and baseline responses would be identical requests
      // (both unfiltered) — reuse the live responses as the baseline instead of re-issuing
      // the same two calls again.
      const [currentRes, prospectRes, baselineCurrentRes, baselineProspectRes] = hasSearchFilter
        ? await Promise.all([
            loadCurrent(currentSearchOnlyFilter),
            loadProspect(prospectSearchOnlyFilter),
            loadCurrent(null),
            loadProspect(null),
          ])
        : await Promise.all([
            loadCurrent(currentSearchOnlyFilter),
            loadProspect(prospectSearchOnlyFilter),
          ]).then(([currentResult, prospectResult]) => [
            currentResult,
            prospectResult,
            currentResult,
            prospectResult,
          ]);

      setTabTotalCounts({
        Current: currentRes?.data?.recordCount ?? 0,
        Prospect: prospectRes?.data?.recordCount ?? 0,
      });
      setBaselineTabTotalCounts({
        Current: baselineCurrentRes?.data?.recordCount ?? 0,
        Prospect: baselineProspectRes?.data?.recordCount ?? 0,
      });
      setProspectAggregateValues((prospectRes?.data?.aggregateValues as Record<string, number>) ?? undefined);
    } catch (err) {
      console.error("Failed to fetch tenant tab counts", err);
    }
  }, [currentSearchOnlyFilter, entityId, isEntity, prospectSearchOnlyFilter]);

  const tenantCounts = tabTotalCounts;

  const totalTenantCounts = tenantCounts;

  const hasAnyTenants = baselineTabTotalCounts.Current > 0 || baselineTabTotalCounts.Prospect > 0;

  // 5. Batch specific state operations
  const handleTabChange = useCallback((tab: string) => {
    if (activeTab === tab) return;
    setActiveTab(tab);
    setFilterKey(null);
    setSelectedFilterLabel("All");
    setSearchTerm("");
  }, [activeTab]);

  const handleSelectChange = useCallback((value: string, label: string) => {
    if (value === "All") {
      setFilterKey(null);
      setSelectedFilterLabel("All");
    } else {
      setFilterKey(value);
      setSelectedFilterLabel(label);
    }
    setSearchTerm("");
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const newFilterKey = params.get("filterKey");
    const newFilterLabel = params.get("filterLabel") || "All";

    if (newFilterKey !== filterKey || newFilterLabel !== selectedFilterLabel) {
      setFilterKey(newFilterKey);
      setSelectedFilterLabel(newFilterLabel);
    }
  }, [search]);

  const filterOptions = useMemo(
    () => [
      { title: "Current", count: tenantCounts.Current },
      { title: "Prospect", count: tenantCounts.Prospect },
    ],
    [tenantCounts],
  );

  const fetchTenantCounts = useCallback(async (filter: string | null) => {
    const countsApi = isEntity ? getApiTenantPropertylistCounts : getApiTenantOrganizationlistCounts;
    const orgIdStr = sessionStorage.getItem(UserLoginData.organization_id);
    const tenantIdStr = sessionStorage.getItem(UserLoginData.tenant_id);
    const orgId = orgIdStr ? parseInt(orgIdStr) : 0;
    const tenantId = tenantIdStr ? parseInt(tenantIdStr) : 0;
    const reqId = isEntity ? entityId! : orgId || tenantId;

    const res = await apiHelper(
      countsApi,
      { showNotification: false },
      reqId,
      1,
      1,
      undefined,
      undefined,
      undefined,
      filter,
      undefined,
    );

    return res?.data?.aggregateValues ?? undefined;
  }, [entityId, isEntity]);

  const { getVisibleOptions, refreshCounts } = useAggregateFilterCounts({
    fetchCounts: fetchTenantCounts,
    searchOnlyFilter: currentSearchOnlyFilter,
    scopeKey: `tenant-filters:${isEntity ? `property:${entityId}` : "organization"}`,
  });

  const visibleSecondaryFilterOptions = useMemo(() => {
    if (activeTab === "Current") {
      return getVisibleOptions([
        { title: ALL_FILTER_KEY, count: resolveAllFilterCount(undefined, tenantCounts.Current) },
        { title: "Lease Expiring Soon" },
        { title: "Lease Expired" },
        { title: "Moving Out" },
        { title: "Past" },
      ]);
    }

    if (activeTab === "Prospect") {
      return getVisibleAggregateOptions(prospectAggregateValues, [
        { title: ALL_FILTER_KEY, count: resolveAllFilterCount(undefined, tenantCounts.Prospect) },
        { title: "In Progress" },
        { title: "Submitted" },
        { title: "Approved" },
        { title: "Past" },
      ]);
    }

    return [];
  }, [activeTab, getVisibleOptions, prospectAggregateValues, tenantCounts.Current, tenantCounts.Prospect]);

  const refreshAllTenants = useCallback(() => {
    currentTenantData.fetchData(1);
    prospectTenantData.fetchData(1);
    fetchTabCounts();
    refreshCounts();
  }, [currentTenantData.fetchData, prospectTenantData.fetchData, fetchTabCounts, refreshCounts]);

  // Load both tabs independently on mount. Tab counts are fetched by the reactive effect
  // below (it also runs once on mount, and again whenever its filter dependencies change) —
  // calling fetchTabCounts() here too would be a redundant duplicate fetch.
  useEffect(() => {
    currentTenantData.fetchData(1);
    prospectTenantData.fetchData(1);
  }, []);

  useEffect(() => {
    fetchTabCounts();
  }, [fetchTabCounts]);

  // Refetch only the active tab when its filters/sort change (skip initial mount)
  useEffect(() => {
    if (skipActiveTabRefetch.current) {
      skipActiveTabRefetch.current = false;
      return;
    }

    if (activeTab === "Current") {
      currentTenantData.fetchData(1);
    } else if (activeTab === "Prospect") {
      prospectTenantData.fetchData(1);
    }
  }, [filterKey, currentSearchOnlyFilter, currentTenantData.currentSortDirection, prospectSearchOnlyFilter, prospectTenantData.currentSortDirection, activeTab]);

  useEffect(() => {
    const unsubscribe = PubSub.subscribe(
      TopicsEnum.RefreshLeaseData.toString(),
      () => {
        setFilterKey(null);
        setSelectedFilterLabel("All");
        refreshAllTenants();
      }
    );

    return () => unsubscribe();
  }, [refreshAllTenants]);

  const commonProps = {
    IsFooter: true,
    dataHookState: {
      rows,
      loading,
      hasMore,
      rowCount,
      fetchData,
      currentPage,
      aggregateValues,
    },
  };

  return (
    <Routes>
      <Route index
        element={
          <PermissionGuard permission="Tenant" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
            <MainContent
              className={className}
              handleSort={handleSort}
              currentSortDirection={currentSortDirection}
              handleSelectChange={handleSelectChange}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterOptions={filterOptions}
              secondaryFilterOptions={visibleSecondaryFilterOptions}
              setActiveTab={handleTabChange} // Passed Batched Handler
              activeTab={activeTab}
              selectedFilterLabel={selectedFilterLabel}
              commonProps={commonProps}
              detailsActiveTab={detailsActiveTab}
              hasAnyTenants={hasAnyTenants}
              totalItemCount={totalTenantCounts[activeTab as keyof typeof totalTenantCounts] || 0}
              showAddButton={canWrite("Tenant")}
            />
          </PermissionGuard>
        }
      />
      <Route path="/create" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.All} fallback={<NoAccess />}>
          <AddTenant fetchData={refreshAllTenants} />
        </PermissionGuard>
      } />
      <Route path="/edit/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.All} fallback={<NoAccess />}>
          <EditTenant fetchData={refreshAllTenants} />
        </PermissionGuard>
      } />
      <Route path="/currenttenantdetails/:id/:unitId" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
          <CurrentTenantDetails2 />
        </PermissionGuard>
      } />
      <Route path="/tenant-onboard/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.All} fallback={<NoAccess />}>
          <LandlordTenantOnboard />
        </PermissionGuard>
      } />
      <Route path="/renew-lease/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.All} fallback={<NoAccess />}>
          <RenewalLease />
        </PermissionGuard>
      } />
      <Route path="/move-out/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.All} fallback={<NoAccess />}>
          <MoveInAndOutContentBlocks isMoveOut={true} />
        </PermissionGuard>
      } />
      <Route path="/review-application/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
          <LeaseApplicationReviewView />
        </PermissionGuard>
      } />
      <Route path="/review-agreement/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
          <LeaseAgreementReviewView />
        </PermissionGuard>
      } />
    </Routes>
  );
};

export default Tenants;